import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import {
  Button,
  FiltersSidebar,
  Icon,
  Sort2,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { clearSortBy, setSortBy } from 'Pods/SearchPage/Store/actions/sortBy'
import { SORT_BY } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

import useBreakpoint from 'Utils/useBreakpoint'

import { buildSortingFiltersConfig } from './constants'

const prefixIcon = <Icon notSpaced svgComponent={Sort2} color="dark" size="small" />

const COMPACT_BREAKPOINTS = ['xs', 'sm']

function Sorting({ showChips = true }) {
  const translate = useTranslate()

  const sortingFiltersConfig = useMemo(() => buildSortingFiltersConfig(translate), [translate])

  const sortLabelByValue = useMemo(() => sortingFiltersConfig[0].filters.reduce((acc, filter) => {
    acc[filter.value] = filter.label
    return acc
  }, {}), [sortingFiltersConfig])

  const sortBy = useSearchPageStore(state => state.sortBy)
  const breakpoint = useBreakpoint()
  // Gate breakpoint-dependent output until mounted so the first client render matches SSR
  // (useBreakpoint is undefined on the server), avoiding a hydration mismatch.
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const hasSelectedAnyFilter = useMemo(() => {
    return sortBy !== SORT_BY.BEST_MATCH
  }, [sortBy])

  // Below md the trigger shows a generic "Sorting" label for every asset type; from
  // 960px up it reflects the selected sort (e.g. "Best Match").
  const placeholder = useMemo(() => {
    if (hasMounted && COMPACT_BREAKPOINTS.includes(breakpoint)) {
      return translate('search_page.filters.sorting.title')
    }

    return sortLabelByValue[sortBy] || translate('search_page.filters.sorting.title')
  }, [hasMounted, breakpoint, sortBy, sortLabelByValue, translate])

  const handleReset = useCallback(() => {
    clearSortBy()
  }, [])

  const handleSortChange = useCallback((_, value) => {
    setSortBy(value)
  }, [])

  const handleNewSort = useCallback(() => {
    if (sortBy === SORT_BY.NEWEST) {
      return handleReset()
    }

    setSortBy(SORT_BY.NEWEST)
  }, [handleReset, sortBy])

  const handleTrendingSort = useCallback(() => {
    if (sortBy === SORT_BY.TRENDING) {
      return handleReset()
    }

    setSortBy(SORT_BY.TRENDING)
  }, [handleReset, sortBy])

  const handleTopSellersSort = useCallback(() => {
    if (sortBy === SORT_BY.TOP_SELLING) {
      return handleReset()
    }

    setSortBy(SORT_BY.TOP_SELLING)
  }, [handleReset, sortBy])

  const selectedSort = useMemo(() => {
    return [sortBy]
  }, [sortBy])

  const content = useMemo(() => (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={sortingFiltersConfig}
        selectedFilters={selectedSort}
        onFiltersChange={handleSortChange}
        isControlled
        hideLastDivider
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
        useNewCheckboxes
        useRadio
      />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={!hasSelectedAnyFilter}
      >
        {translate('search_page.filters.sorting.reset')}
      </Button>
    </>
  ), [selectedSort, handleSortChange, handleReset, hasSelectedAnyFilter, sortingFiltersConfig, translate])

  return (
    <div className="sorting">
      {showChips && (
        <div className="sorting__chips">
          <Button
            type="secondary"
            onClick={handleNewSort}
            isActive={sortBy === SORT_BY.NEWEST}
            customClass="filter-wrapper__button sorting__chip--new"
          >
            {translate('search_page.filters.sorting.new')}
          </Button>
          <Button
            type="secondary"
            onClick={handleTrendingSort}
            isActive={sortBy === SORT_BY.TRENDING}
            customClass="filter-wrapper__button sorting__chip--trending"
          >
            {translate('search_page.filters.sorting.trending')}
          </Button>
          <Button
            type="secondary"
            onClick={handleTopSellersSort}
            isActive={sortBy === SORT_BY.TOP_SELLING}
            customClass="filter-wrapper__button sorting__chip--top-sellers"
          >
            {translate('search_page.filters.sorting.top_sellers')}
          </Button>
        </div>
      )}
      <FiltersWrapper
        placeholder={placeholder}
        prefixIcon={prefixIcon}
        content={content}
        hasSelectedAnyFilters={hasSelectedAnyFilter}
        positionHorizontal="left"
        closingDropdownClassName="filter-wrapper sorting__select"
        contentClassName="sorting__dropdown-content"
        size="medium"
      />
    </div>
  )
}

Sorting.propTypes = {
  showChips: bool,
}

export default memo(Sorting)
